<template>
  <div>
    <my-header></my-header>

    <div class="events-box">
      <h2>Events</h2>
      <event-filter
        :status="status"
        @status-update-requested="updateStatus"
        :periodWindow="periodWindow"
      />

      <loading-indicator v-if="isLoading"/>

      <p v-if="message !== null" class="mt-4">
        {{message}}
      </p>
    </div>
  </div>
</template>

<script>
import notifier from '../notifier.js';
import { HTTP } from '../http-common.js';

import MyHeader from './Header.vue';
import EventFilter from './EventFilter.vue';
import LoadingIndicator from './LoadingIndicator.vue';

/**
 * @typedef { import('../model/event_status.js').EventStatus } EventStatus
 */

import { TIME_UNIT_MONTH } from '../model/event_time_filter.js';
import { getBegin        } from '../model/event_time_filter.js';
import { getEnd          } from '../model/event_time_filter.js';

import { convertMomentToStamp } from '../util.js';

const LOADING_STATUS__INIT    = 1;
const LOADING_STATUS__LOADING = 2;
const LOADING_STATUS__NORMAL  = 3;
const LOADING_STATUS__ERROR   = 4;

export default {
  name: 'Events',

  components: {
    MyHeader,
    EventFilter,
    LoadingIndicator,
  },

  data() {
    const DURATION_1_MONTH = {timeUnit: TIME_UNIT_MONTH, amount: 1};

    return {
      loadingStatus: LOADING_STATUS__INIT,
      eventGroups: null,

      //-----------------------------------------------------------------------
      // Filter
      //-----------------------------------------------------------------------

      /** @type {EventStatus | null} */
      status: null,

      periodWindow: {
        pastDuration:   DURATION_1_MONTH,
        futureDuration: DURATION_1_MONTH,
      },

      //-----------------------------------------------------------------------
    };
  },

  computed: {
    pastDuration() {
      return this.periodWindow.pastDuration;
    },

    futureDuration() {
      return this.periodWindow.futureDuration;
    },

    /** @returns {boolean} */
    isLoading() {
      return this.loadingStatus === LOADING_STATUS__LOADING;
    },

    /** @returns {boolean} */
    isLoadedNormal() {
      return this.loadingStatus === LOADING_STATUS__NORMAL;
    },

    /** @returns {?string} */
    message() {
      if(this.isLoadedNormal) {
        const eventGroups = this.eventGroups;
        if(eventGroups !== null && eventGroups.length === 0) {
          return 'No events.';
        }
      }

      return null;
    },
  },

  created() {
    this.loadEvents();
  },

  watch: {
    status() {
      this.loadEvents();
    },

    pastDuration() {
      this.loadEvents();
    },

    futureDuration() {
      this.loadEvents();
    },
  },

  methods: {
    loadEvents() {
      notifier.deleteSenderNotifications(this);

      this.loadingStatus = LOADING_STATUS__LOADING;

      const url = '/events';

      const requestConf = {
        params: {}
      };

      const status = this.status;
      if(status !== null) {
        requestConf.params.status = status;
      }

      const now = convertMomentToStamp(new Date());

      const pastDuration = this.pastDuration;
      if(pastDuration !== null) {
        requestConf.params.begin = getBegin(now, pastDuration);
      }

      const futureDuration = this.futureDuration;
      if(futureDuration !== null) {
        requestConf.params.end = getEnd(now, futureDuration);
      }

      HTTP.get(url, requestConf)
      .then(response => {
        const data = response.data.data;
        
        if(data === null) {
          notifier.showError('Invalid server response. data === null.', {sender: this});
          return;
        }

        this.eventGroups = data;

        this.loadingStatus = LOADING_STATUS__NORMAL;
      })
      .catch(err => {
        this.loadingStatus = LOADING_STATUS__ERROR;
        notifier.showError(err, {sender: this});
      });
    },

    updateStatus(newValue) {
      this.status = newValue;
    },
  },
};
</script>

<style scoped>
.events-box {
  padding-top: 1em;
  margin-left: 1em;
  margin-bottom: 1rem;
}
</style>
